import { Check, CornerDownRight, AlertCircle, Sparkles } from 'lucide-react';
import { AKCENT as SECTION_COLOR } from '../../../lib/spizirnaUi';

/** Návrh jistý natolik, že stačí potvrdit bez otevírání panelu. */
const JISTY = 88;

const DUVODY = {
    'presna-shoda': 'název i varianta sedí',
    'nazev+slovnik': 'varianta přes slovník',
    'nazev+varianta': 'jediná varianta',
    'podobnost': 'podobný název',
    'chybi-varianta': 'variantu neznáme',
    'historicky-odhad': 'starý odhad, neověřený',
};

/**
 * Jeden název z Objedname.
 *
 * Nejvíc místa má **tržba za 90 dní**, protože podle ní se určuje pořadí
 * práce: 290 hranolek je míň peněz než čtyřicet pizz. Počet kusů je vedle
 * ní, drobně — je to kontext, ne kritérium.
 *
 * Tlačítko „potvrdit" je přímo na řádku, ne až v panelu. Nejčastější akce na
 * téhle obrazovce je „ano, tenhle je to" a dřív pro ni nebyla cesta kratší
 * než otevřít pole, vyhledat a vybrat recepturu, kterou systém už znal.
 */
export default function RadekMapovani({ radek, vybrany, isDark, styly, onVybrat, onPotvrdit, skupinaSkryta = false }) {
    const { secondaryColor, mutedColor } = styly;
    const navrh = radek.navrh;
    const jisty = navrh?.recept && (navrh.skore ?? 0) >= JISTY;
    const hotovy = radek.stav === 'potvrzeno';

    return (
        <div
            onClick={onVybrat}
            className="flex items-center gap-3 px-3 py-2 cursor-pointer"
            style={{
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.045)' : 'var(--border-subtle, rgba(0,0,0,0.04))'}`,
                background: vybrany
                    ? (isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.09)')
                    : 'transparent',
                boxShadow: vybrany ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none',
            }}
        >
            <div className="min-w-0" style={{ flex: '1 1 40%' }}>
                <div className="flex items-baseline gap-1.5 flex-wrap">
                    {/* Pořadí z cizího menu. Tlumeně, protože to není identita
                        jídla — ale vidět být musí: v Objedname se položka jmenuje
                        „04.Slaninová“ a bez té čtyřky se seznam hůř prochází. */}
                    {radek.poradi && (
                        <span className="text-[12px] font-semibold tnum" style={{ color: mutedColor }}>
                            {radek.poradi}.
                        </span>
                    )}
                    <span className="text-[13px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                        {radek.nazev}
                    </span>
                    {/* Varianta zvlášť, protože zvlášť se i porovnává — a protože
                        „32cm" vedle názvu je to, co na řádku odlišuje sourozence. */}
                    {radek.varianta && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                            style={{ background: `${SECTION_COLOR}1F`, color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}33` }}>
                            {radek.varianta}
                        </span>
                    )}
                    {radek.typ === 'pridavek' && (
                        <span className="text-[9.5px] uppercase font-bold tracking-wide" style={{ color: mutedColor }}>
                            ingredience
                        </span>
                    )}
                    {/* Skupina z Objedname. U přídavků je to combo struktura —
                        „Nápoj výhodně" říká, že to není topping, ale slot
                        v menu. Bez ní se čedarová omáčka nedá rozhodnout. */}
                    {radek.skupina_obj && !skupinaSkryta && (
                        <span className="text-[10px] truncate" style={{ color: mutedColor }}>
                            {radek.skupina_obj}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                    <CornerDownRight size={11} style={{ color: mutedColor, flexShrink: 0 }} />
                    <Cil radek={radek} navrh={navrh} hotovy={hotovy}
                        secondaryColor={secondaryColor} mutedColor={mutedColor} />
                </div>
            </div>

            <div className="text-right flex-shrink-0" style={{ width: 106 }}>
                {/* Tři různé stavy, které se nesmí plést:
                    · nikdy neprodáno  → kuchyň to ještě neviděla,
                    · v menu bez tržby → prodává se pomalu, ale je to živé,
                    · tržba            → běžný případ. */}
                {radek.nove ? (
                    <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold"
                        style={{
                            background: `${SECTION_COLOR}1F`,
                            border: `1px solid ${SECTION_COLOR}40`,
                            color: SECTION_COLOR,
                        }}
                        title="Je v menu Objedname, ale nikdy si to nikdo neobjednal"
                    >
                        <Sparkles size={10} />
                        nové v menu
                    </span>
                ) : radek.kusu === 0 ? (
                    <span className="text-[10px]" style={{ color: mutedColor }}
                        title="Je v aktuálním menu Objedname, ale za 90 dní se neprodalo">
                        v menu · bez tržby
                    </span>
                ) : (
                    <>
                        <div className="text-[13px] font-bold tnum" style={{ color: 'var(--text-primary)' }}>
                            {Math.round(radek.trzba).toLocaleString('cs-CZ')} Kč
                        </div>
                        <div className="text-[10px] tnum" style={{ color: mutedColor }}>
                            {radek.kusu}× / 90 dní
                        </div>
                    </>
                )}
            </div>

            <div className="flex-shrink-0" style={{ width: 104 }}>
                {hotovy ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold"
                        style={{ color: '#10B981' }}>
                        <Check size={13} strokeWidth={3} /> hotovo
                    </span>
                ) : jisty ? (
                    <button
                        onClick={(e) => { e.stopPropagation(); onPotvrdit(); }}
                        className="w-full inline-flex items-center justify-center gap-1 text-[11.5px] font-bold rounded-lg"
                        style={{
                            height: 30, cursor: 'pointer',
                            background: 'rgba(16,185,129,0.14)',
                            border: '1px solid rgba(16,185,129,0.4)',
                            color: '#10B981',
                        }}
                        title={`${navrh.skore} % — ${DUVODY[navrh.duvod] ?? navrh.duvod}`}
                    >
                        <Check size={13} strokeWidth={3} />
                        Potvrdit
                    </button>
                ) : (
                    <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: mutedColor }}>
                        <AlertCircle size={12} />
                        rozhodnout
                    </span>
                )}
            </div>
        </div>
    );
}

/**
 * Na co to míří. Tři různé věci, které se nesmí plést:
 * hotové rozhodnutí, návrh k potvrzení, a „nevíme".
 */
function Cil({ radek, navrh, hotovy, secondaryColor, mutedColor }) {
    if (hotovy && radek.recept) {
        return (
            <span className="text-[11.5px] truncate" style={{ color: secondaryColor }}>
                {radek.recept.nazev}
                {radek.recept.varianta && <span style={{ color: mutedColor }}> · {radek.recept.varianta}</span>}
            </span>
        );
    }
    if (hotovy) {
        return <span className="text-[11.5px]" style={{ color: mutedColor }}>{popisCile(radek)}</span>;
    }
    if (navrh?.recept) {
        return (
            <span className="text-[11.5px] truncate" style={{ color: secondaryColor }}>
                {navrh.recept.nazev}
                {navrh.recept.varianta && <span style={{ color: mutedColor }}> · {navrh.recept.varianta}</span>}
                <span className="ml-1.5 tnum" style={{ color: mutedColor }}>
                    {navrh.skore} % · {DUVODY[navrh.duvod] ?? navrh.duvod}
                </span>
            </span>
        );
    }
    return (
        <span className="text-[11.5px] italic" style={{ color: mutedColor }}>
            bez návrhu — protějšek nenalezen
        </span>
    );
}

function popisCile(radek) {
    if (radek.cilovy_typ === 'ignorovat') return 'ignorováno';
    if (radek.cilovy_typ === 'poznamka') return 'jde do poznámky jídla';
    if (radek.cilovy_typ === 'naklad') return `náklad: ${radek.naklad_typ}`;
    if (radek.cilovy_typ === 'combo' && radek.combo_skupina) return `combo: ${radek.combo_skupina.nazev}`;
    return '—';
}
